// Avatar Wardrobe Service — Supabase CRUD for avatars, credits, marketplace, gifting

import { supabase } from '../config/supabase';
import { vrmBlocksTransfer } from '../avatar/vrm/vrmFormat';

/** Uploaded VRMs carry their license in creation_params: refuse what it forbids. */
async function assertVrmTransferAllowed(avatarId: string, kind: 'sell' | 'gift') {
  const { data } = await supabase.from('avatars').select('creation_params').eq('id', avatarId).maybeSingle();
  if (vrmBlocksTransfer(data?.creation_params)[kind]) {
    throw new Error(kind === 'sell' ? 'This VRM avatar’s license doesn’t allow it to be sold.' : 'This VRM avatar’s license doesn’t allow it to be given away.');
  }
}

// ---- Types ----

export interface Avatar {
  id: string;
  owner_id: string;
  name: string;
  model_url: string;
  thumbnail_url: string | null;
  reference_image_url: string | null;
  animations: Record<string, string>; // {idle, walking, running, jumping}
  creation_params: Record<string, string>; // {style, gender, clothing, ...}
  is_active: boolean;
  is_listed: boolean;
  list_price: number;
  created_at: string;
  updated_at: string;
  active_texture_url?: string | null;
  /** The worn look's own rendered picture (joined from avatar_looks). */
  active_look?: { thumbnail_url: string | null } | null;
  // Joined fields (marketplace)
  owner_username?: string;
}

export interface Credits {
  user_id: string;
  balance: number;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: number;
  type: 'purchase' | 'sale' | 'gift' | 'grant';
  related_avatar_id: string | null;
  related_user_id: string | null;
  created_at: string;
}

// ---- My Avatars ----

export async function fetchMyAvatars(): Promise<Avatar[]> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) return [];
  const { data, error } = await supabase
    .from('avatars')
    .select('*, active_look:avatar_looks!avatars_active_look_id_fkey(thumbnail_url)')
    .eq('owner_id', user.id)
    .order('created_at', { ascending: false });

  if (error) throw error;
  // NPCs are built through the same pipeline but belong to worlds, not the wardrobe.
  return (data || []).filter((a: Avatar) => a.creation_params?.npc !== 'true');
}

/** Characters this account built as NPCs (reusable in any world). */
export async function fetchMyNpcAvatars(): Promise<Avatar[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('avatars')
    .select('*')
    .eq('owner_id', user.id)
    .eq('creation_params->>npc', 'true')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getActiveAvatar(): Promise<Avatar | null> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) return null;
  const { data, error } = await supabase
    .from('avatars')
    .select('*')
    .eq('owner_id', user.id)
    .eq('is_active', true)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function saveNewAvatar(params: {
  name?: string;
  model_url: string;
  thumbnail_url?: string;
  reference_image_url?: string;
  animations?: Record<string, string>;
  creation_params?: Record<string, string>;
  setActive?: boolean;
}): Promise<Avatar> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('avatars')
    .insert({
      owner_id: user.id,
      name: params.name || `Avatar ${new Date().toLocaleDateString()}`,
      model_url: params.model_url,
      thumbnail_url: params.thumbnail_url || null,
      reference_image_url: params.reference_image_url || null,
      animations: params.animations || {},
      creation_params: params.creation_params || {},
      is_active: false,
    })
    .select()
    .single();

  if (error) throw error;
  if (params.setActive !== false) {
    await setActiveAvatar(data.id);
    data.is_active = true;
  }
  return data;
}

export async function setActiveAvatar(avatarId: string): Promise<void> {
  const { error } = await supabase.rpc('set_active_avatar', { p_avatar_id: avatarId });
  if (error) throw error;
}

export async function updateAvatar(avatarId: string, updates: {
  name?: string;
  is_listed?: boolean;
  list_price?: number;
  thumbnail_url?: string;
}): Promise<void> {
  if (updates.is_listed) await assertVrmTransferAllowed(avatarId, 'sell');
  const { error } = await supabase
    .from('avatars')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', avatarId);
  if (error) throw error;
}

// Persist the user's Avatar Pose slider values and current clip set on the
// active wardrobe row (inside the animations jsonb as __pose), so they follow
// the avatar across devices, sessions, and to other viewers.
export async function savePoseAndAnimations(): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return; // anonymous sessions keep localStorage-only tuning
  const active = await getActiveAvatar();
  if (!active) return;
  let anims: Record<string, unknown> = {};
  try { anims = JSON.parse(localStorage.getItem('eb-avatar-animations') || '{}') || {}; } catch { /* keep {} */ }
  try {
    const pose = JSON.parse(localStorage.getItem('gathrxr.poseTune') || 'null');
    if (pose) anims.__pose = pose;
  } catch { /* no pose */ }
  const { error } = await supabase
    .from('avatars')
    .update({ animations: anims, updated_at: new Date().toISOString() })
    .eq('id', active.id);
  if (error) throw error;
}

export async function deleteAvatar(avatarId: string): Promise<void> {
  const { error } = await supabase
    .from('avatars')
    .delete()
    .eq('id', avatarId);
  if (error) throw error;
}

// ---- Credits ----

export async function getCredits(): Promise<number> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;

  const { data, error } = await supabase
    .from('credits')
    .select('balance')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) throw error;

  // Auto-create credits row if missing (existing users before trigger)
  if (!data) {
    const { data: created, error: createErr } = await supabase
      .from('credits')
      .insert({ user_id: user.id, balance: 100 })
      .select('balance')
      .single();
    if (createErr) {
      console.warn('[Credits] Failed to auto-create:', createErr);
      return 0;
    }
    return created.balance;
  }

  return data.balance;
}

export async function getTransactions(): Promise<Transaction[]> {
  const { data, error } = await supabase
    .from('transactions')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data || [];
}

// ---- Marketplace ----

export async function fetchMarketplaceAvatars(opts?: {
  limit?: number;
  offset?: number;
}): Promise<Avatar[]> {
  const query = supabase
    .from('avatars')
    .select('*')
    .eq('is_listed', true)
    .order('created_at', { ascending: false })
    .range(opts?.offset || 0, (opts?.offset || 0) + (opts?.limit || 20) - 1);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export async function purchaseAvatar(avatarId: string): Promise<void> {
  const { error } = await supabase.rpc('purchase_avatar', { p_avatar_id: avatarId });
  if (error) throw error;
}

export async function listAvatar(avatarId: string, price: number): Promise<void> {
  await updateAvatar(avatarId, { is_listed: true, list_price: price });
}

export async function unlistAvatar(avatarId: string): Promise<void> {
  await updateAvatar(avatarId, { is_listed: false, list_price: 0 });
}

// ---- Gifting ----

export async function giftAvatar(avatarId: string, toUserId: string, message?: string): Promise<string> {
  await assertVrmTransferAllowed(avatarId, 'gift');
  const { data, error } = await supabase.rpc('gift_avatar', {
    p_avatar_id: avatarId,
    p_to_user_id: toUserId,
    p_message: message || null,
  });
  if (error) throw error;
  return data;
}

export async function searchUsers(query: string): Promise<{ id: string; username: string }[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username')
    .ilike('username', `%${query}%`)
    .limit(10);

  if (error) throw error;
  return data || [];
}

// ---- Migration: import localStorage avatar to Supabase ----

export async function migrateLocalStorageAvatar(): Promise<Avatar | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  // Check if user already has avatars in DB
  const { count } = await supabase
    .from('avatars')
    .select('id', { count: 'exact', head: true })
    .eq('owner_id', user.id);

  if (count && count > 0) return null; // Already has avatars

  const modelUrl = localStorage.getItem('eb-avatar-url');
  if (!modelUrl || modelUrl === 'skip') return null;

  const thumbnailUrl = localStorage.getItem('eb-avatar-thumbnail');
  let animations: Record<string, string> = {};
  try {
    const stored = localStorage.getItem('eb-avatar-animations');
    if (stored) animations = JSON.parse(stored);
  } catch {}

  console.log('[AvatarService] Migrating localStorage avatar to Supabase');

  const avatar = await saveNewAvatar({
    name: 'My First Avatar',
    model_url: modelUrl,
    thumbnail_url: thumbnailUrl || undefined,
    animations,
    setActive: true,
  });

  return avatar;
}
